/**
 * User SSH Keys service — VSNP-680 / re-arch C.
 * Per-user SSH public-key store. The cert generation flow uses these
 * instead of asking the user to paste their pubkey every time.
 *
 * Also wraps the issued-cert list / revoke endpoints (VSNP-685 /
 * workstream A).
 */

import api from './api';

export interface UserSshKey {
  id: string;
  label: string;
  publicKey: string;
  fingerprint: string;
  autoRenew: boolean;
  // Where the matching private key lives on the user's filesystem. Always
  // populated — backend falls back to per-algo default (id_ed25519 / id_rsa /
  // id_ecdsa) when the row has no explicit override.
  privateKeyPath: string;
  privateKeyPathExplicit: boolean;
  createdAt: string | null;
  lastUsedAt: string | null;
}

export interface IssuedSshCert {
  certFingerprint: string;
  certSerial: string;
  userId: string;
  userEmail: string | null;
  tenantId: string;
  userKeyId: string | null;
  principals: string[];
  edgeIds: string[] | null;
  validAfter: number;
  validBefore: number;
  caFingerprint: string | null;
  revokedAt: string | null;
  revokedBy: string | null;
  revokeReason: string | null;
  createdAt: string | null;
}

const userSshKeysService = {
  list: async (): Promise<UserSshKey[]> => {
    const res = await api.get('/users/me/ssh-keys') as any;
    // successResponse wraps as { success: true, data: { keys } }
    return res?.data?.keys ?? res?.keys ?? [];
  },

  add: async (
    label: string,
    publicKey: string,
    autoRenew = false,
    privateKeyPath?: string,
  ): Promise<UserSshKey> => {
    return await api.post('/users/me/ssh-keys', {
      label, publicKey, autoRenew, privateKeyPath,
    }) as any;
  },

  update: async (
    id: string,
    patchBody: { label?: string; autoRenew?: boolean; privateKeyPath?: string | null },
  ): Promise<UserSshKey> => {
    const res = await api.patch(`/users/me/ssh-keys/${id}`, patchBody) as any;
    return res?.data ?? res;
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/users/me/ssh-keys/${id}`);
  },

  // VSNP-685 / workstream A: issued-cert list + revoke
  listIssuedCerts: async (): Promise<IssuedSshCert[]> => {
    const res = await api.get('/users/me/ssh-certs') as any;
    return res?.data?.certs ?? res?.certs ?? [];
  },

  revokeCert: async (serial: string, reason?: string): Promise<void> => {
    await api.post(`/users/me/ssh-certs/${serial}/revoke`, { reason });
  },
};

export default userSshKeysService;
